import { useState, useMemo } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';

/**
 * Word Cloud Display Component
 * Custom animated word cloud visualization
 */

const WordCloudDisplay = ({ activity, responses = [], showProjection = false, onExport }) => {
  const [selectedWord, setSelectedWord] = useState(null);

  // Process responses into word frequency data
  const wordData = useMemo(() => {
    if (!responses || responses.length === 0) return [];

    const wordCounts = {};
    const wordOriginal = {}; // Store the first occurrence's original casing

    responses.forEach((response) => {
      const originalWord = response.word?.trim();
      if (!originalWord) return;
      const key = originalWord.toLowerCase();
      wordCounts[key] = (wordCounts[key] || 0) + 1;
      // Keep the original casing from the first response
      if (!wordOriginal[key]) {
        wordOriginal[key] = originalWord;
      }
    });

    // Convert to array and sort by frequency
    const words = Object.entries(wordCounts)
      .map(([key, count]) => ({ word: wordOriginal[key], count }))
      .sort((a, b) => b.count - a.count);

    // Calculate sizes
    const maxCount = words[0]?.count || 1;
    const minCount = words[words.length - 1]?.count || 1;

    return words.map((item, index) => {
      // Scale font size between 14px and 64px (adjusted for better visibility)
      const normalizedSize = maxCount === minCount ? 0.5 : (item.count - minCount) / (maxCount - minCount);
      const fontSize = Math.max(14, Math.min(64, 14 + normalizedSize * 50));

      // Modern color palette based on frequency/rank
      const colors = [
        '#ff8a00', // primary orange (top word)
        '#ff6b35', // bright coral
        '#f7931e', // vibrant orange
        '#8b5cf6', // purple
        '#3b82f6', // blue
        '#10b981', // green
        '#06b6d4', // cyan
        '#6366f1', // indigo
        '#ec4899', // pink
        '#64748b', // slate
      ];

      return {
        ...item,
        fontSize,
        color: colors[Math.min(index, colors.length - 1)],
        rank: index + 1,
      };
    });
  }, [responses]);

  // Top words for sidebar
  const topWords = wordData.slice(0, 10);

  // Generate positions for words with better distribution
  const positionedWords = useMemo(() => {
    if (wordData.length === 0) return [];

    // For small number of words, use a grid-like layout
    if (wordData.length <= 10) {
      const positions = [
        { x: 50, y: 30 },  // top center
        { x: 25, y: 50 },  // left center
        { x: 75, y: 50 },  // right center
        { x: 50, y: 70 },  // bottom center
        { x: 30, y: 30 },  // top left
        { x: 70, y: 30 },  // top right
        { x: 30, y: 70 },  // bottom left
        { x: 70, y: 70 },  // bottom right
        { x: 50, y: 50 },  // center
        { x: 15, y: 50 },  // far left
      ];

      return wordData.map((word, index) => ({
        ...word,
        x: positions[index % positions.length].x,
        y: positions[index % positions.length].y,
      }));
    }

    // For more words, use spiral layout
    return wordData.map((word, index) => {
      const goldenAngle = Math.PI * (3 - Math.sqrt(5)); // ~137.5 degrees
      const angle = index * goldenAngle;
      const radius = Math.sqrt(index + 1) * 12;
      const x = 50 + Math.cos(angle) * Math.min(radius, 40);
      const y = 50 + Math.sin(angle) * Math.min(radius, 35);

      return {
        ...word,
        x: Math.max(10, Math.min(90, x)),
        y: Math.max(10, Math.min(90, y)),
      };
    });
  }, [wordData]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">{activity?.title || 'Word Cloud'}</h3>
            <p className="text-sm text-neutral-600">
              {responses.length} {responses.length === 1 ? 'response' : 'responses'} • {wordData.length} unique{' '}
              {wordData.length === 1 ? 'word' : 'words'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Export Button */}
            {onExport && (
              <Button variant="secondary" size="sm" onClick={() => onExport(wordData, 'wordcloud')}>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </Button>
            )}
          </div>
        </div>

        {/* Word Cloud Display */}
        {wordData.length > 0 ? (
          <div className="bg-neutral-50 rounded-lg p-8 relative border border-neutral-200" style={{ minHeight: '400px' }}>
            {positionedWords.map((word, index) => (
              <div
                key={`${word.word}-${index}`}
                style={{
                  position: 'absolute',
                  left: `${word.x}%`,
                  top: `${word.y}%`,
                  fontSize: `${word.fontSize}px`,
                  color: word.color,
                  fontWeight: word.rank <= 3 ? '700' : '600',
                  cursor: 'pointer',
                  transform: 'translate(-50%, -50%)',
                  userSelect: 'none',
                }}
                onClick={() => setSelectedWord(word)}
                className="hover:opacity-75 transition-opacity"
              >
                {word.word}
              </div>
            ))}

            {/* Selected Word Popup */}
            {selectedWord && (
              <div
                className="absolute top-4 right-4 bg-white p-4 rounded-lg shadow-lg border border-neutral-200 z-20"
              >
                <button
                  onClick={() => setSelectedWord(null)}
                  className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <p className="text-xl font-bold mb-3" style={{ color: selectedWord.color }}>
                  {selectedWord.word}
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-neutral-500">Count</span>
                    <span className="font-bold text-neutral-900">{selectedWord.count}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-neutral-500">Rank</span>
                    <span className="font-bold text-neutral-900">#{selectedWord.rank}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-neutral-500">Percentage</span>
                    <span className="font-bold text-neutral-900">
                      {((selectedWord.count / responses.length) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 bg-neutral-50 rounded-lg border border-neutral-200">
            <div className="text-center">
              <svg className="w-12 h-12 mx-auto text-neutral-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              <p className="text-neutral-600">No responses yet</p>
              <p className="text-sm text-neutral-500">Words will appear as participants submit</p>
            </div>
          </div>
        )}
      </Card>

      {/* Top Words List */}
      {topWords.length > 0 && (
        <Card padding="md">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <h4 className="text-base font-semibold text-neutral-900">Top Words</h4>
          </div>
          <div className="space-y-2">
            {topWords.map((word, index) => (
              <div
                key={word.word}
                className="flex items-center gap-3 p-3 rounded-lg bg-white border border-neutral-200 hover:border-neutral-300 transition-colors cursor-pointer"
                onClick={() => setSelectedWord(word)}
              >
                {/* Rank Badge */}
                <div className="flex items-center justify-center w-7 h-7 rounded bg-neutral-100 font-semibold text-sm text-neutral-600">
                  {index + 1}
                </div>

                {/* Word */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate text-neutral-900">
                    {word.word}
                  </p>
                </div>

                {/* Progress Bar and Count */}
                <div className="flex items-center gap-3">
                  <div className="w-20 sm:w-32 bg-neutral-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary-500 transition-all duration-300"
                      style={{ width: `${(word.count / topWords[0].count) * 100}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-neutral-900 w-8 text-right">{word.count}</span>
                    <span className="text-neutral-500 w-12 text-right hidden sm:inline">
                      ({((word.count / responses.length) * 100).toFixed(0)}%)
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Statistics */}
      {wordData.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <p className="text-sm text-neutral-600 mb-1">Responses</p>
            <p className="text-2xl font-bold text-neutral-900">{responses.length}</p>
          </div>

          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <p className="text-sm text-neutral-600 mb-1">Unique Words</p>
            <p className="text-2xl font-bold text-neutral-900">{wordData.length}</p>
          </div>

          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <p className="text-sm text-neutral-600 mb-1">Top Word</p>
            <p className="text-xl font-bold text-neutral-900 truncate">{wordData[0]?.word || '-'}</p>
          </div>
        </div>
      )}

      {/* Projection Mode Indicator */}
      {showProjection && (
        <div className="fixed top-4 right-4 bg-neutral-800 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm">
          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
          <span>Projecting</span>
        </div>
      )}
    </div>
  );
};

export default WordCloudDisplay;
